import { ReactNode } from 'react';

export type Align = 'left' | 'right' | 'center';

export interface Column<T> {
  key: string;
  title: ReactNode;
  align?: Align;
  width?: string | number;
  render: (row: T, index: number) => ReactNode;
  sortable?: boolean;
  sortDirection?: 'asc' | 'desc';
  onSort?: () => void;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowKey: (row: T, index: number) => string;
  loading?: boolean;
  emptyMessage?: ReactNode;
  rowClassName?: (row: T, index: number) => string;
  onRowClick?: (row: T, index: number) => void;
  /** When set, rows become <a> links so ctrl+click / middle-click open in a new tab. */
  rowHref?: (row: T, index: number) => string;
}

const alignClass: Record<Align, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

export function DataTable<T>({
  columns,
  data,
  rowKey,
  loading,
  emptyMessage = 'No results',
  rowClassName,
  onRowClick,
  rowHref,
}: DataTableProps<T>) {
  return (
    <div className="flex-1 min-h-0 overflow-auto bg-panel relative">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                style={col.width ? { width: col.width } : undefined}
                className={`text-[10px] uppercase tracking-wider text-text-3 font-semibold py-2.5 px-4 border-b border-border bg-bg sticky top-0 z-10 ${
                  alignClass[col.align ?? 'left']
                } ${col.sortable ? 'cursor-pointer select-none hover:text-text' : ''}`}
                onClick={col.sortable ? col.onSort : undefined}
              >
                <span className="inline-flex items-center gap-1">
                  {col.title}
                  {col.sortable && (
                    <span className="text-[8px] opacity-60">
                      {col.sortDirection === 'asc' ? '▲' : col.sortDirection === 'desc' ? '▼' : '↕'}
                    </span>
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={loading && data.length > 0 ? 'opacity-50 pointer-events-none transition-opacity' : 'transition-opacity'}>
          {loading && data.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="text-center py-12 text-text-3 text-[12px]">
                Loading…
              </td>
            </tr>
          )}
          {!loading && data.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="text-center py-12 text-text-3 text-[12px]">
                {emptyMessage}
              </td>
            </tr>
          )}
          {data.map((row, idx) => {
            const extra = rowClassName?.(row, idx) ?? '';
            const href = rowHref?.(row, idx);
            const clickable = !!(onRowClick || href);
            return (
              <tr
                key={rowKey(row, idx)}
                onClick={clickable ? (e) => {
                  // Let ctrl/meta/middle-click fall through to <a> for new-tab behavior
                  if (href && (e.ctrlKey || e.metaKey || e.button === 1)) return;
                  onRowClick?.(row, idx);
                } : undefined}
                className={`border-b border-border hover:bg-bg ${clickable ? 'cursor-pointer' : ''} ${extra}`}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`py-2.5 px-4 text-[13px] text-text align-middle ${alignClass[col.align ?? 'left']}`}
                  >
                    {href ? (
                      <a
                        href={href}
                        onClick={(e) => {
                          // Plain click → prevent navigation, let tr onClick handle it
                          if (!e.ctrlKey && !e.metaKey && e.button === 0) e.preventDefault();
                        }}
                        className="block text-inherit no-underline"
                        draggable={false}
                      >
                        {col.render(row, idx)}
                      </a>
                    ) : (
                      col.render(row, idx)
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {loading && data.length > 0 && (
        <div className="sticky bottom-0 left-0 right-0 h-[3px] bg-accent/15 overflow-hidden z-20">
          <div className="h-full w-1/3 bg-accent rounded-full" style={{ animation: 'loading-slide 1s ease-in-out infinite' }} />
          <style>{`@keyframes loading-slide { 0% { transform: translateX(-100%); } 100% { transform: translateX(400%); } }`}</style>
        </div>
      )}
    </div>
  );
}
